import { NODE_MERGE_D, MICRO_EDGE, MIN_AREA_MM2, EPS } from "../constants";
import { Geo } from "../utils/geo";
import { addEdgePrim, removeEdgePrim } from "./dcel";
import { mkId } from "../utils/coords";
import { getMode } from "../constants/drawingModes";

/** Get mode-appropriate thresholds — falls back to architectural constants */
function getThresholds(st) {
  const mode = getMode(st);
  return {
    nodeMerge: mode.nodeMerge ?? NODE_MERGE_D,
    microEdge: mode.microEdge ?? MICRO_EDGE,
    minArea:   mode.minArea   ?? MIN_AREA_MM2,
  };
}

// ─── Validation ───────────────────────────────────────────────────────────────
export function validateDCEL(st) {
  const errors = [];
  const push = (rule, msg, ids = []) => errors.push({ rule, msg, ids });

  for (const he of Object.values(st.halfEdges)) {
    const tw = st.halfEdges[he.twinId];
    if (!tw) push("A", `HE ${he.id.slice(0, 8)} missing twin`, [he.id]);
    else if (tw.twinId !== he.id) push("A", `HE twin.twin≠self`, [he.id, tw.id]);
  }
  for (const he of Object.values(st.halfEdges)) {
    if (!st.nodes[he.originId]) push("B", `HE ${he.id.slice(0, 8)} missing origin`, [he.id]);
    const tw = st.halfEdges[he.twinId];
    if (tw && tw.originId === he.originId) push("B", `HE self-loop`, [he.id]);
  }
  for (const e of Object.values(st.edges)) {
    const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
    if (!ab) push("C", `Edge ${e.id.slice(0, 8)} missing HE_A`, [e.id]);
    if (!ba) push("C", `Edge ${e.id.slice(0, 8)} missing HE_B`, [e.id]);
    if (ab && ba && (ab.twinId !== ba.id || ba.twinId !== ab.id)) push("C", `Edge twin mismatch`, [e.id]);
  }
  for (const n of Object.values(st.nodes))
    if (!Number.isInteger(n.x) || !Number.isInteger(n.y)) push("D", `Node non-integer`, [n.id]);

  return errors;
}

// ─── Heal pipeline steps ──────────────────────────────────────────────────────
function enforceIntegers(st) {
  const nn = {}; let ch = false;
  for (const [id, n] of Object.entries(st.nodes)) {
    const ix = Math.round(n.x), iy = Math.round(n.y);
    if (ix !== n.x || iy !== n.y) ch = true;
    nn[id] = { ...n, x: ix, y: iy };
  }
  return ch ? { ...st, nodes: nn } : st;
}

function deduplicateNodes(st) {
  const { nodeMerge } = getThresholds(st);
  const list = Object.values(st.nodes), remap = {}, dead = new Set();
  for (let i = 0; i < list.length; i++) {
    if (dead.has(list[i].id)) continue;
    for (let j = i + 1; j < list.length; j++) {
      if (dead.has(list[j].id)) continue;
      if (Geo.dsq(list[i], list[j]) < nodeMerge * nodeMerge) {
        remap[list[j].id] = list[i].id;
        dead.add(list[j].id);
      }
    }
  }
  if (!dead.size) return st;
  const nn = {};
  for (const [id, n] of Object.entries(st.nodes)) if (!dead.has(id)) nn[id] = { ...n, halfEdgeIds: [] };
  const nh = {};
  for (const [id, he] of Object.entries(st.halfEdges)) {
    const orig = remap[he.originId] || he.originId;
    if (!nn[orig]) continue;
    nh[id] = { ...he, originId: orig };
    nn[orig].halfEdgeIds.push(id);
  }
  return { ...st, nodes: nn, halfEdges: nh };
}

function dissolveCollinear(st) {
  let s = st, changed = true, pass = 0;
  while (changed && pass < 20) {
    changed = false; pass++;
    for (const [nId, node] of Object.entries(s.nodes)) {
      if (node.halfEdgeIds.length !== 2) continue;
      const out = node.halfEdgeIds.map(h => s.halfEdges[h]).filter(h => h && h.originId === nId);
      if (out.length !== 2) continue;
      const [he1, he2] = out;
      const tw1 = s.halfEdges[he1.twinId], tw2 = s.halfEdges[he2.twinId];
      if (!tw1 || !tw2) continue;
      const nA = s.nodes[tw1.originId], nB = s.nodes[tw2.originId];
      if (!nA || !nB) continue;
      if (Geo.cosABC(nA, node, nB) > -0.999) continue;
      const e1 = s.edges[he1.edgeId];
      s = removeEdgePrim(he1.edgeId, s);
      if (s.edges[he2.edgeId]) s = removeEdgePrim(he2.edgeId, s);
      const nn2 = { ...s.nodes }; delete nn2[nId]; s = { ...s, nodes: nn2 };
      s = addEdgePrim(nA.id, nB.id, e1?.type, e1?.thickness, e1?.height, s);
      changed = true; break;
    }
  }
  return s;
}

function removeMicroEdges(st) {
  const { microEdge } = getThresholds(st);
  let s = st;
  for (const [eId, e] of Object.entries(s.edges)) {
    const ab = s.halfEdges[e.halfEdgeA], ba = s.halfEdges[e.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = s.nodes[ab.originId], nB = s.nodes[ba.originId];
    if (!nA || !nB) continue;
    if (Geo.d(nA, nB) >= microEdge) continue;
    s = removeEdgePrim(eId, s);
    const nh = { ...s.halfEdges };
    for (const [hid, he] of Object.entries(nh)) if (he.originId === nB.id) nh[hid] = { ...he, originId: nA.id };
    const nn = { ...s.nodes }; delete nn[nB.id];
    nn[nA.id] = { ...nA, halfEdgeIds: Object.values(nh).filter(h => h.originId === nA.id).map(h => h.id) };
    s = { ...s, nodes: nn, halfEdges: nh };
  }
  return s;
}

// ─── Face rebuild ─────────────────────────────────────────────────────────────
function pickNextHE(inc, st) {
  const tw = st.halfEdges[inc.twinId]; if (!tw) return null;
  const jn = st.nodes[tw.originId], on = st.nodes[inc.originId];
  if (!jn || !on) return null;
  const iv = { x: jn.x - on.x, y: jn.y - on.y };
  const cands = (jn.halfEdgeIds || []).map(id => st.halfEdges[id]).filter(h => h && h.id !== tw.id);
  if (!cands.length) return tw;
  let best = null, bestA = Infinity;
  for (const he of cands) {
    const dest = st.nodes[st.halfEdges[he.twinId]?.originId]; if (!dest) continue;
    const ov = { x: dest.x - jn.x, y: dest.y - jn.y }, a = Geo.ang(iv, ov);
    if (a < bestA) { bestA = a; best = he; }
  }
  return best || tw;
}

function rebuildFaces(st, dirtyAABB = null) {
  const done = new Set(), nxtMap = {}, faces = {};
  const heCount = Object.keys(st.halfEdges).length;
  if (dirtyAABB) {
    for (const [fid, f] of Object.entries(st.faces)) {
      if (!f.loop || !f.aabb) continue;
      if (!Geo.aabbOvlp(f.aabb, dirtyAABB)) {
        faces[fid] = f;
        f.loop.forEach(id => done.add(id));
        for (let i = 0; i < f.loop.length; i++) nxtMap[f.loop[i]] = f.loop[(i + 1) % f.loop.length];
      }
    }
  }
  for (const startId of Object.keys(st.halfEdges)) {
    if (done.has(startId)) continue;
    const wkSeen = new Set(), chain = [], chainNxt = {};
    let curId = startId, closed = false;
    for (let i = 0; i <= heCount + 2; i++) {
      if (wkSeen.has(curId)) { closed = curId === startId && chain.length >= 3; break; }
      wkSeen.add(curId); chain.push(curId);
      const cur = st.halfEdges[curId]; if (!cur) break;
      const nxt = pickNextHE(cur, st); if (!nxt) break;
      chainNxt[curId] = nxt.id; curId = nxt.id;
    }
    if (!closed) continue;
    const pts = chain.map(id => { const he = st.halfEdges[id]; return he ? st.nodes[he.originId] : null; }).filter(Boolean);
    if (pts.length < 3) continue;
    const area = Geo.area(pts); if (area < getThresholds(st).minArea) continue;
    chain.forEach(id => done.add(id)); Object.assign(nxtMap, chainNxt);
    const fid = mkId("f");
    faces[fid] = { id: fid, halfEdgeId: chain[0], area: Math.round(area), centroid: Geo.centroid(pts), loop: chain, aabb: Geo.aabb(pts) };
  }
  const hes = {};
  for (const [id, he] of Object.entries(st.halfEdges)) hes[id] = { ...he, nextId: nxtMap[id] || null, faceId: null };
  for (const f of Object.values(faces)) for (const hid of f.loop) if (hes[hid]) hes[hid] = { ...hes[hid], faceId: f.id };
  return { halfEdges: hes, faces };
}

// ─── [T1] Instrumented heal ───────────────────────────────────────────────────
export function healGraph(st, mutAABB = null) {
  const changes = [];
  const n0 = Object.keys(st.nodes).length;
  const e0 = Object.keys(st.edges).length;

  let s = enforceIntegers(st);
  s = deduplicateNodes(s);
  const n1 = Object.keys(s.nodes).length;
  if (n1 < n0) changes.push(`Merged ${n0 - n1} duplicate node${n0 - n1 > 1 ? "s" : ""}`);

  s = dissolveCollinear(s);
  const n2 = Object.keys(s.nodes).length;
  if (n2 < n1) changes.push(`Dissolved ${n1 - n2} collinear junction${n1 - n2 > 1 ? "s" : ""}`);

  s = removeMicroEdges(s);
  const e1 = Object.keys(s.edges).length;
  if (e1 < e0) changes.push(`Removed ${e0 - e1} micro-edge${e0 - e1 > 1 ? "s" : ""}`);

  const { halfEdges, faces } = rebuildFaces(s, mutAABB);
  s = { ...s, halfEdges, faces };
  const errors = validateDCEL(s);
  if (errors.length > 0 && changes.length === 0) changes.push(`${errors.length} integrity error${errors.length > 1 ? "s" : ""} detected`);

  if (changes.length > 0) {
    const entry = { ts: Date.now(), changes };
    s = { ...s, _healLog: [...(s._healLog || []).slice(-19), entry] };
  }

  return { state: s, errors, changes };
}
